import Checkbox from '@/components/Checkbox/Checkbox';
import { SimpleShowBadge, VideoBadge } from '@/pages/home/components/Common';
import { hasError, isGenerating } from '@/pages/home/utils';
import { useAnalytics } from '@/services/analytics';
import { useSelectedVideos } from '@/state/video-library/videoLibrary';
import { formatDate } from '@/utils/date';
import { MixPanelEvent } from '@/utils/types';
import { LibraryItem } from '@d-id/types';
import styled from '@emotion/styled';
import { useMemo, useState } from 'react';
import { CheckboxContainer, CoreVideoTile } from './CoreVideoTile';
import { getSimpleShowMenuOptions, getSimpleShowPrimaryAction, getSimpleShowState } from './simpleshow-utils';
import { SimpleShowActionButton } from './SimpleShowActionButton';
import { SimpleShowVideoOptionsList } from './SimpleShowVideoOptionsList';
import { VideoTileInfo } from './VideoTileInfo';

const BadgeContainer = styled.div`
    display: flex;
    gap: 6px;
    z-index: 2;
    position: absolute;
    bottom: 12px;
    left: 12px;
`;

const StyledSimpleShowBadge = styled(SimpleShowBadge)`
    position: unset !important;
`;

const StyledDraftBadge = styled(VideoBadge)`
    position: unset !important;
`;

const DisabledCheckboxContainer = styled(CheckboxContainer)`
    > div > span {
        cursor: not-allowed;
    }
`;

const TileWrapper = styled.div<{
    isSelectMode: boolean;
}>`
    ${({ isSelectMode }) =>
        isSelectMode &&
        `
        .CheckboxContainer {
            opacity: 1;
        }
    `}
`;

interface Props {
    item: LibraryItem;
    origin: string;
}

export const SimpleShowVideoTile = ({ item, origin }: Props) => {
    const mixpanel = useAnalytics();
    const [keepHover, setKeepHover] = useState(false);
    const { selectMode, selected } = useSelectedVideos();

    const formattedDate = formatDate(item.created_at);
    const isLoading = isGenerating(item);
    const isError = hasError(item);
    const isDone = !isLoading && !isError;

    const shouldShowCheckbox = selectMode && selected.size > 0;

    const simpleShowState = useMemo(() => getSimpleShowState(item), [item]);
    const action = useMemo(() => getSimpleShowPrimaryAction(simpleShowState), [simpleShowState]);
    const menuOptions = useMemo(() => getSimpleShowMenuOptions(simpleShowState), [simpleShowState]);

    const handleTileClick = (_e: React.MouseEvent<HTMLDivElement>) => {
        if (action) {
            mixpanel?.track(action.eventName, {
                event: MixPanelEvent.CLICK,
                from: origin,
                videoId: item.id,
                editorType: 'video-maker',
            });
            window.open(action.url, '_blank', 'noopener,noreferrer');
        }
    };

    const shouldShowDraftBadge = !simpleShowState.isRendered;
    const badge = (
        <BadgeContainer>
            <StyledSimpleShowBadge testId={`simpleshow-badge-${item.name}`} />
            {shouldShowDraftBadge && <StyledDraftBadge testId={`simpleshow-draft-badge-${item.name}`} isDraft />}
        </BadgeContainer>
    );

    const controls = !selectMode ? (
        <>
            {isDone && action && <SimpleShowActionButton action={action} item={item} origin={origin} />}
            <SimpleShowVideoOptionsList
                video={item}
                menuOptions={menuOptions}
                onOpen={() => setKeepHover(true)}
                onClose={() => setKeepHover(false)}
                origin={origin}
            />
        </>
    ) : null;

    const tileInfo = (
        <VideoTileInfo
            item={item}
            videoName={item.name}
            setVideoName={() => {}}
            handleBlur={() => Promise.resolve()}
            formattedDate={formattedDate}
            isError={isError}
            readOnly={true}
        />
    );

    const checkbox = shouldShowCheckbox ? (
        <DisabledCheckboxContainer className="CheckboxContainer">
            <Checkbox
                checked={false}
                onClick={e => {
                    e.stopPropagation();
                }}
                disabled={true}
            />
        </DisabledCheckboxContainer>
    ) : null;

    return (
        <TileWrapper isSelectMode={selectMode}>
            <CoreVideoTile
                item={item}
                hovered={keepHover}
                isDone={isDone}
                isError={isError}
                isLoading={isLoading}
                badge={badge}
                controls={controls}
                tileInfo={tileInfo}
                checkbox={checkbox}
                onClick={keepHover || shouldShowCheckbox ? undefined : handleTileClick}
                disableHover={selectMode}
                showSelectBorder={selectMode}
                selectCursor="default"
            />
        </TileWrapper>
    );
};
